/*
  a11y-fixes-3.js  (accessibility remediation epic, round 2/3; v10 2026-10-26)
  Third small accessibility layer (search, moving carousels, clean-up), loaded after a11y-fixes-2.js.
  Remove this file + its <script> tag in layout/theme.liquid to roll back.
  Version check: browser console shows "[a11y-fixes-3] v10 loaded".

  E. Search (SHO-1005): "Search" button (no toggle states), panel = dialog "Search", "Close search",
     "Clear search", result count announced after typing, focus returns to the search button on close.
  F. Auto-moving carousels (SHO-1007) - <slider-grid autoplay-mode="...">: "Pause animation" / "Play animation"
     button before each one (visible on keyboard focus); pauses while focus is inside / mouse is over;
     prefers-reduced-motion respected.
  H. Clean-up: filter switches named after their label ("In stock only"); cart total not a heading.
     NOT changed on purpose: colour contrast of the crossed-out old price and inactive search tabs.
     (v10) Pages without a main heading (collections, most info pages) get a visually hidden <h1> with the
     page name (from the document title), as the first element of <main>.
  I. Content sliders (<image-slider>, e.g. the feature gallery on the Smart Garden 9 / 9 PRO pages)
     (W3C/WAI-ARIA APG carousel pattern):
     - Region with role description "carousel", named "<product name> features".
     - Visible Previous/Next buttons moved before the slides in the document (absolutely positioned, so no
       visual change); named "Previous slide" / "Next slide".
     - Only the current slide (text panel + photo/video) is exposed; others aria-hidden until shown.
       The slide group ("N of M") is on the inner .image-slider__content element; panels are not Tab stops.
     - After Previous/Next, the new slide is announced: "Slide 2 of 9: Engineered simplicity".
     - Slide dots (.solid-dots-carousel) hidden from screen readers and Tab.
     - Videos in slides: named "Video: <slide heading>", posters hidden, "Pause videos" / "Play videos"
       button after Previous/Next (stays paused across slide changes), prefers-reduced-motion = paused.
     - The theme's carousel containers and slide panels carry click handlers (NVDA said "clickable");
       they are role=none, semantics on inner elements.
*/
(function () {
  'use strict';

  var VERSION = 'v10';
  window.A11Y_FIXES3_VERSION = VERSION;
  try { console.info('[a11y-fixes-3] ' + VERSION + ' loaded'); } catch (e) { /* ignore */ }

  var PANEL_ID = 'SearchSidebar';
  var TOGGLE_ATTRS = ['aria-expanded', 'aria-haspopup', 'aria-controls'];

  function isVisible(el) {
    return !!(el && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
  }

  function focusIsLost() {
    var a = document.activeElement;
    return !a || a === document.body || a === document.documentElement;
  }

  // Shared live region (created by a11y-fixes.js; created here if missing).
  function announce(msg) {
    var el = document.getElementById('A11yLiveRegion');
    if (!el) {
      el = document.createElement('div');
      el.id = 'A11yLiveRegion';
      el.className = 'visually-hidden';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('aria-atomic', 'true');
      document.body.appendChild(el);
    }
    el.textContent = '';
    setTimeout(function () { el.textContent = msg; }, 150);
  }

  function setAttr(el, name, value) {
    if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function removeAttrs(el, names) {
    for (var i = 0; i < names.length; i++) if (el.hasAttribute(names[i])) el.removeAttribute(names[i]);
  }

  function hideIcons(el) {
    var svgs = el.querySelectorAll('svg');
    for (var i = 0; i < svgs.length; i++) svgs[i].setAttribute('aria-hidden', 'true');
  }

  /* =========================== E. Search =========================== */
  function panel() {
    return document.getElementById(PANEL_ID);
  }

  function panelIsOpen() {
    var p = panel();
    return !!(p && p.classList.contains('is-opened'));
  }

  // Header buttons that open the search panel (not the close button inside the panel).
  function searchOpeners() {
    var p = panel();
    return Array.prototype.filter.call(
      document.querySelectorAll('button[data-sidebar-id="' + PANEL_ID + '"]'),
      function (b) { return !(p && p.contains(b)); }
    );
  }

  var wasOpen = false;
  var opener = null;

  function fixSearch() {
    var p = panel();
    var open = panelIsOpen();

    var openers = searchOpeners();
    for (var i = 0; i < openers.length; i++) {
      setAttr(openers[i], 'aria-label', 'Search');
      removeAttrs(openers[i], TOGGLE_ATTRS);
    }

    if (!p) return;
    setAttr(p, 'role', 'dialog');
    setAttr(p, 'aria-label', 'Search');
    if (open) setAttr(p, 'aria-modal', 'true');
    else if (p.hasAttribute('aria-modal')) p.removeAttribute('aria-modal');
    var aside = p.closest('aside');
    setAttr(aside, 'role', 'none');

    var closes = p.querySelectorAll('[class*="search-sidebar__close-button"]');
    for (var c = 0; c < closes.length; c++) {
      setAttr(closes[c], 'aria-label', 'Close search');
      removeAttrs(closes[c], TOGGLE_ATTRS);
      hideIcons(closes[c]);
    }
    var resets = p.querySelectorAll('[data-search-field-reset-button]');
    for (var r = 0; r < resets.length; r++) {
      setAttr(resets[r], 'aria-label', 'Clear search');
      hideIcons(resets[r]);
    }

    if (wasOpen && !open) {
      var target = opener && document.contains(opener) && isVisible(opener) ? opener : null;
      if (!target) {
        for (var j = 0; j < openers.length; j++) {
          if (isVisible(openers[j])) { target = openers[j]; break; }
        }
      }
      setTimeout(function () {
        if (target && document.activeElement !== target &&
            (focusIsLost() || (panel() && panel().contains(document.activeElement)))) target.focus();
      }, 120);
    }
    wasOpen = open;
  }

  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('button[data-sidebar-id="' + PANEL_ID + '"]');
    var p = panel();
    if (b && !(p && p.contains(b))) opener = b;
  }, true);

  var typingTimer = null;

  function countResults(p) {
    var suggestions = Array.prototype.filter.call(p.querySelectorAll('.search-sidebar-section__query'), isVisible).length;
    var products = Array.prototype.filter.call(
      p.querySelectorAll('vertical-product-card, horizontal-product-card, product-card'), isVisible).length;
    return { suggestions: suggestions, products: products };
  }

  function plural(n, word) {
    return n + ' ' + word + (n === 1 ? '' : 's');
  }

  document.addEventListener('input', function (e) {
    var p = panel();
    if (!p || !e.target || !p.contains(e.target) || e.target.tagName.toLowerCase() !== 'input') return;
    clearTimeout(typingTimer);
    var query = (e.target.value || '').trim();
    if (!query) return;
    typingTimer = setTimeout(function () {
      var pp = panel();
      if (!pp || !panelIsOpen()) return;
      var input = pp.querySelector('input');
      if (!input || (input.value || '').trim() !== query) return;
      var n = countResults(pp);
      if (!n.suggestions && !n.products) {
        announce('No results for ' + query + '.');
      } else {
        var parts = [];
        if (n.suggestions) parts.push(plural(n.suggestions, 'suggestion'));
        if (n.products) parts.push(plural(n.products, 'product'));
        announce(parts.join(' and ') + ' for ' + query + '.');
      }
    }, 1400);
  }, true);

  /* =================== F. Auto-moving carousels =================== */
  var PAUSE_CLASS = 'a11y-carousel-pause';
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function injectPauseStyles() {
    if (document.getElementById('A11yCarouselStyles')) return;
    var s = document.createElement('style');
    s.id = 'A11yCarouselStyles';
    s.textContent =
      '.' + PAUSE_CLASS + '{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;' +
      'clip:rect(0 0 0 0);white-space:nowrap;border:0;}' +
      '.' + PAUSE_CLASS + ':focus{position:relative;display:inline-block;width:auto;height:auto;margin:0 0 8px;' +
      'padding:4px 10px;overflow:visible;clip:auto;z-index:5;background:#fff;color:#1a1a1a;' +
      'border:2px solid #1a1a1a;border-radius:4px;font-size:13px;line-height:1.3;}' +
      '.a11y-visually-hidden{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;}';
    document.head.appendChild(s);
  }

  function isAutoMoving(sg) {
    var mode = sg.getAttribute('autoplay-mode');
    return !!mode && mode !== 'false' && mode !== 'none';
  }

  function isSeamless(sg) {
    return sg.getAttribute('autoplay-mode') === 'seamless';
  }

  // The theme's internal function that (re)starts the seamless movement.
  function seamlessStarter(sg) {
    if (sg.__a11yStarterKey && typeof sg[sg.__a11yStarterKey] === 'function') return sg[sg.__a11yStarterKey];
    var keys = Object.keys(sg);
    for (var i = 0; i < keys.length; i++) {
      var f = sg[keys[i]];
      if (typeof f === 'function' && /seamlessRunning\s*=\s*(!0|true)/.test(f.toString())) {
        sg.__a11yStarterKey = keys[i];
        return f;
      }
    }
    return null;
  }

  function stopCarousel(sg) {
    try { if (typeof sg.stopAutoplay === 'function') sg.stopAutoplay(); } catch (e) { /* ignore */ }
  }

  function startCarousel(sg) {
    if (reducedMotion) return;
    try {
      sg.autoplayForcedStopped = false;
      if (isSeamless(sg)) {
        var start = seamlessStarter(sg);
        if (start) { sg.seamlessRunning = false; start.call(sg); }
      } else if (typeof sg.startAutoplay === 'function') {
        sg.startAutoplay();
      }
    } catch (e) { /* ignore */ }
  }

  function updatePauseButton(sg) {
    var btn = sg.__a11yPauseBtn;
    if (!btn) return;
    var label = sg.__a11yUserPaused ? 'Play animation' : 'Pause animation';
    if (btn.textContent !== label) btn.textContent = label;
  }

  function themeForcedStop(sg) {
    return !!sg.autoplayForcedStopped && !sg.__a11yTempPaused;
  }

  function setupCarousels() {
    var sliders = document.querySelectorAll('slider-grid[autoplay-mode]');
    if (!sliders.length) return;
    injectPauseStyles();
    for (var i = 0; i < sliders.length; i++) {
      var sg = sliders[i];
      if (!isAutoMoving(sg) || sg.__a11yCarouselDone) continue;
      sg.__a11yCarouselDone = true;

      if (reducedMotion) {
        sg.__a11yUserPaused = true;
        setTimeout(stopCarousel.bind(null, sg), 300);
      }

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = PAUSE_CLASS;
      btn.textContent = 'Pause animation';
      sg.__a11yPauseBtn = btn;
      sg.insertAdjacentElement('beforebegin', btn);
      btn.addEventListener('click', (function (s) {
        return function () {
          if (s.__a11yUserPaused) {
            s.__a11yUserPaused = false;
            startCarousel(s);
            announce('Animation playing');
          } else {
            s.__a11yUserPaused = true;
            stopCarousel(s);
            announce('Animation paused');
          }
          updatePauseButton(s);
        };
      })(sg));

      (function (s) {
        function tempPause() {
          if (s.__a11yUserPaused || themeForcedStop(s)) return;
          s.__a11yTempPaused = true;
          stopCarousel(s);
        }
        function tempResume() {
          if (!s.__a11yTempPaused) return;
          s.__a11yTempPaused = false;
          if (s.__a11yUserPaused) return;
          startCarousel(s);
        }
        s.addEventListener('focusin', tempPause);
        s.addEventListener('focusout', function (e) {
          if (!e.relatedTarget || !s.contains(e.relatedTarget)) {
            if (s.__a11yUsedArrows) { s.__a11yTempPaused = false; s.__a11yUserPaused = true; updatePauseButton(s); return; }
            tempResume();
          }
        });
        s.addEventListener('mouseenter', tempPause);
        s.addEventListener('mouseleave', function () {
          if (s.contains(document.activeElement)) return;
          if (s.__a11yUsedArrows) { s.__a11yTempPaused = false; s.__a11yUserPaused = true; updatePauseButton(s); return; }
          tempResume();
        });
        s.addEventListener('click', function (e) {
          if (e.target && e.target.closest && e.target.closest('[data-direction]')) s.__a11yUsedArrows = true;
        }, true);
      })(sg);

      updatePauseButton(sg);
    }
  }

  /* ========================== H. Clean-up ========================== */
  function fixFilterSwitches() {
    var inputs = document.querySelectorAll('input[type="checkbox"][id$="-out-of-stock-switch"], .filters__label input[type="checkbox"]');
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (inp.getAttribute('aria-label') || inp.getAttribute('aria-labelledby')) continue;
      var holder = inp.closest('.filters__label') || inp.closest('.filters__item');
      var text = holder ? (holder.innerText || holder.textContent || '').replace(/\s+/g, ' ').trim() : '';
      if (text) inp.setAttribute('aria-label', text);
    }
  }

  function fixCartTotals() {
    var hs = document.querySelectorAll('h1[class*="subtotal__totals"], h2[class*="subtotal__totals"], h3[class*="subtotal__totals"], h4[class*="subtotal__totals"], h5[class*="subtotal__totals"], h6[class*="subtotal__totals"]');
    for (var i = 0; i < hs.length; i++) setAttr(hs[i], 'role', 'none');
  }

  // Pages without a main heading: add a visually hidden <h1> with the page name.
  function pageName() {
    var t = (document.title || '').split(/\s[|\u2013\u2014-]\s/)[0];
    return (t || '').replace(/\s+/g, ' ').trim();
  }

  function fixMainHeading() {
    var main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;
    if (document.getElementById('A11yPageHeading')) return;
    var hasH1 = Array.prototype.some.call(main.querySelectorAll('h1, [role="heading"][aria-level="1"]'), function (h) {
      return !h.closest('[aria-hidden="true"]');
    });
    if (hasH1) return;
    var name = pageName();
    if (!name) return;
    injectPauseStyles();
    var h1 = document.createElement('h1');
    h1.id = 'A11yPageHeading';
    h1.className = 'a11y-visually-hidden';
    h1.textContent = name;
    main.insertBefore(h1, main.firstChild);
  }

  /* ====================== I. Content sliders ====================== */
  function productName() {
    var h1 = document.querySelector('main h1');
    return h1 ? (h1.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function slideHeading(panelEl) {
    var h = panelEl && panelEl.querySelector('h1, h2, h3, h4, h5, h6');
    return h ? (h.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function selectedIndex(list) {
    for (var i = 0; i < list.length; i++) if (list[i].classList.contains('is-selected')) return i;
    return 0;
  }

  function setHidden(el, hidden) {
    if (hidden) setAttr(el, 'aria-hidden', 'true');
    else if (el.hasAttribute('aria-hidden')) el.removeAttribute('aria-hidden');
  }

  function setupSliderVideos(sl, carousel, panels) {
    var videos = sl.querySelectorAll('.image-slider__slide video');
    if (!videos.length) return;
    injectPauseStyles();

    var slides = sl.querySelectorAll('.image-slider__slide');
    for (var s = 0; s < slides.length; s++) {
      var v = slides[s].querySelector('video');
      if (!v) continue;
      var h = slideHeading(panels[s]);
      setAttr(v, 'aria-label', 'Video' + (h ? ': ' + h : ''));
      var posters = slides[s].querySelectorAll('img');
      for (var p = 0; p < posters.length; p++) setAttr(posters[p], 'aria-hidden', 'true');
      if (!v.__a11yPlayGuard) {
        v.__a11yPlayGuard = true;
        v.addEventListener('play', function (e) { if (sl.__a11yVideosPaused) e.target.pause(); });
      }
    }

    if (reducedMotion && sl.__a11yVideosPaused === undefined) sl.__a11yVideosPaused = true;
    if (sl.__a11yVideosPaused) for (var k = 0; k < videos.length; k++) if (!videos[k].paused) videos[k].pause();

    var btn = sl.__a11yVideoBtn;
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = PAUSE_CLASS;
      sl.__a11yVideoBtn = btn;
      btn.addEventListener('click', function () {
        var vids = sl.querySelectorAll('.image-slider__slide video');
        sl.__a11yVideosPaused = !sl.__a11yVideosPaused;
        for (var i = 0; i < vids.length; i++) {
          if (sl.__a11yVideosPaused) vids[i].pause();
          else if (vids[i].closest('.image-slider__slide').classList.contains('is-selected')) {
            var pr = vids[i].play(); if (pr && pr.catch) pr.catch(function () {});
          }
        }
        btn.textContent = sl.__a11yVideosPaused ? 'Play videos' : 'Pause videos';
        announce(sl.__a11yVideosPaused ? 'Videos paused' : 'Videos playing');
      });
    }
    btn.textContent = sl.__a11yVideosPaused ? 'Play videos' : 'Pause videos';
    var nextBtn = carousel && Array.prototype.filter.call(carousel.children, function (c) {
      return c.matches && c.matches('[data-direction="next"]');
    })[0];
    if (nextBtn && nextBtn.nextElementSibling !== btn) nextBtn.insertAdjacentElement('afterend', btn);
    else if (!nextBtn && !btn.parentElement) sl.insertAdjacentElement('afterbegin', btn);
  }

  // Generic containers with click handlers inside a slider: no semantics (avoids NVDA "clickable").
  var SLIDER_CONTAINERS = 'carousel-component, carousel-dots, .carousel__viewport, .carousel__container, .card-carousel__viewport';

  function fixImageSliders() {
    var sliders = document.querySelectorAll('image-slider');
    for (var i = 0; i < sliders.length; i++) {
      var sl = sliders[i];
      var name = productName();
      setAttr(sl, 'role', 'region');
      setAttr(sl, 'aria-roledescription', 'carousel');
      setAttr(sl, 'aria-label', name ? name + ' features' : 'Features');

      var containers = sl.querySelectorAll(SLIDER_CONTAINERS);
      for (var c = 0; c < containers.length; c++) {
        if (!containers[c].hasAttribute('tabindex')) setAttr(containers[c], 'role', 'none');
      }

      // Visible arrows first in the document (they are absolutely positioned, so the layout is unchanged).
      var carousel = sl.querySelector('carousel-component.image-slider-slideshow') || sl.querySelector('carousel-component');
      if (carousel) {
        var own = Array.prototype.filter.call(carousel.children, function (ch) { return ch.matches && ch.matches('[data-direction]'); });
        var prev = own.filter(function (a) { return a.getAttribute('data-direction') === 'prev'; })[0];
        var next = own.filter(function (a) { return a.getAttribute('data-direction') === 'next'; })[0];
        if (prev && next && (carousel.firstElementChild !== prev || prev.nextElementSibling !== next)) {
          carousel.insertBefore(next, carousel.firstElementChild);
          carousel.insertBefore(prev, next);
        }
      }

      var arrows = sl.querySelectorAll('[data-direction]');
      for (var a = 0; a < arrows.length; a++) {
        var label = arrows[a].getAttribute('data-direction') === 'prev' ? 'Previous slide' : 'Next slide';
        setAttr(arrows[a], 'aria-label', label);
        hideIcons(arrows[a]);
        if (!arrows[a].__a11yAnnounce) {
          arrows[a].__a11yAnnounce = true;
          arrows[a].addEventListener('click', (function (slider) {
            return function () {
              setTimeout(function () {
                fixImageSliders();
                var ps = slider.querySelectorAll('.image-slider__content-wrapper');
                if (!ps.length) return;
                var idx = selectedIndex(ps);
                var h = slideHeading(ps[idx]);
                announce('Slide ' + (idx + 1) + ' of ' + ps.length + (h ? ': ' + h : ''));
              }, 450);
            };
          })(sl));
        }
      }

      var textCarousels = sl.querySelectorAll('.image-slider__dots-carousel');
      for (var t = 0; t < textCarousels.length; t++) {
        if (textCarousels[t].getAttribute('aria-hidden') === 'true') textCarousels[t].removeAttribute('aria-hidden');
      }

      var panels = sl.querySelectorAll('.image-slider__content-wrapper');
      var sel = selectedIndex(panels);
      for (var p = 0; p < panels.length; p++) {
        var pn = panels[p];
        setAttr(pn, 'role', 'none');
        removeAttrs(pn, ['aria-roledescription', 'aria-label']);
        if (pn.getAttribute('tabindex') !== '-1') pn.setAttribute('tabindex', '-1');
        setHidden(pn, p !== sel);
        var inner = pn.querySelector('.image-slider__content') || pn.firstElementChild;
        if (inner) {
          setAttr(inner, 'role', 'group');
          setAttr(inner, 'aria-roledescription', 'slide');
          setAttr(inner, 'aria-label', (p + 1) + ' of ' + panels.length);
        }
      }

      var photos = sl.querySelectorAll('.image-slider__slide');
      var psel = selectedIndex(photos);
      for (var f = 0; f < photos.length; f++) setHidden(photos[f], f !== psel);

      var dots = sl.querySelectorAll('.solid-dots-carousel');
      for (var d = 0; d < dots.length; d++) {
        setAttr(dots[d], 'aria-hidden', 'true');
        var focusables = dots[d].querySelectorAll('a[href], button, [tabindex]');
        for (var k = 0; k < focusables.length; k++) if (focusables[k].getAttribute('tabindex') !== '-1') focusables[k].setAttribute('tabindex', '-1');
      }

      setupSliderVideos(sl, carousel, panels);
    }
  }

  /* ---------- Run on load and whenever the page changes ---------- */
  var scheduled = false;
  function run() {
    scheduled = false;
    try { fixSearch(); } catch (err) { /* never break the page */ }
    try { setupCarousels(); } catch (err) { /* never break the page */ }
    try { fixFilterSwitches(); fixCartTotals(); fixMainHeading(); } catch (err) { /* never break the page */ }
    try { fixImageSliders(); } catch (err) { /* never break the page */ }
  }

  var observer = new MutationObserver(function () {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(run);
    }
  });

  var attrObserver = new MutationObserver(function (records) {
    var slider = false;
    for (var i = 0; i < records.length; i++) {
      var t = records[i].target;
      if (t.id === PANEL_ID) { try { fixSearch(); } catch (e) { /* ignore */ } }
      if (t.classList && (t.classList.contains('image-slider__content-wrapper') || t.classList.contains('image-slider__slide'))) slider = true;
    }
    if (slider) { try { fixImageSliders(); } catch (e) { /* ignore */ } }
  });

  function init() {
    run();
    observer.observe(document.body, { childList: true, subtree: true });
    attrObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
